import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Button, Input, Select, Space, Tabs } from 'antd';
import { SearchOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { ColumnType } from 'antd/es/table';
import styled from 'styled-components';
import { useSnackbar } from '../contexts/SnackbarContext/SnackbarProvider';
import {
  CreateUserByAdminType,
  UserSearchParams,
  UserType,
} from '../types/types';
import CustomButton from '../components/common/Button';
import { CustomTable } from '../components/common/CustomTable';
import UserForm from '../components/features/User/UserForm';
import {
  COLORS,
  FONT_WEIGHTS,
  TEXT_SIZES,
  USER_ENTITY_STATUS_OPTIONS,
} from '../utils/constants';
import {
  createUserAdminRequest,
  deleteUserRequest,
  searchUsersRequest,
  updateUserAdminRequest,
} from '../api/requests/users';
import DeleteModal from '../components/shared/DeleteModal';
import { CustomTag, StyledActionButton, StyledSpaceWrapper } from './Facility';
import { Outlet, useNavigate, useOutlet } from 'react-router-dom';
import { USERMANAGEMENTPATH } from '../routes/routePaths';
import { debounce } from '../utils/utils';
import { UserRole } from '../enums/enums';

const { Search } = Input;
const { Option } = Select;

const TAB_ROLE_MAP = {
  admin: UserRole.Admin,
  appUser: UserRole.User,
  facilityOwner: UserRole.FacilityOwner,
  eventOrganizer: UserRole.EventOrganizer,
} as const;

type UserTabKey = keyof typeof TAB_ROLE_MAP;

const USER_TABS: { key: UserTabKey; label: string }[] = [
  { key: 'admin', label: 'Admin' },
  { key: 'appUser', label: 'User' },
  { key: 'facilityOwner', label: 'Facility Owner' },
  { key: 'eventOrganizer', label: 'Event Organizer' },
];

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const outlet = useOutlet();
  const snackbar = useSnackbar();
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const [selectedUserToDelete, setSelectedUserToDelete] =
    useState<UserType | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const DEFAULT_USER_TAB: UserTabKey = Object.keys(
    TAB_ROLE_MAP,
  )[0] as UserTabKey;
  const activeTabRef = useRef<UserTabKey>(DEFAULT_USER_TAB);
  const [status, setStatus] = useState<number | undefined>();

  const pageSize = 8;

  const handleTabChange = (key: string) => {
    activeTabRef.current = key as UserTabKey;
    setCurrentPage(1);
    fetchUsers(1, searchText, status);
  };

  const onDeleteUser = useCallback(async () => {
    if (!selectedUserToDelete) return;
    try {
      setLoading(true);
      setOpenDeleteModal(false);
      const response = await deleteUserRequest(selectedUserToDelete.userId);
      if (response.isSuccess) {
        snackbar.success('User deleted successfully');
        fetchUsers(currentPage, searchText, status);
      } else {
        snackbar.error(response?.error || 'Failed to delete user');
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Failed to delete user');
      console.error('Delete user error:', error);
    } finally {
      setSelectedUserToDelete(null);
      setLoading(false);
    }
  }, [selectedUserToDelete, snackbar, currentPage, searchText]);

  const fetchUsers = useCallback(
    async (page = 1, searchTerm = '', status?: number) => {
      try {
        setLoading(true);
        const params: UserSearchParams = {
          PageNumber: Math.max(0, page - 1),
          PageSize: pageSize,
          SearchQuery: searchTerm || undefined,
          EntityStatus: status,
          userRole: TAB_ROLE_MAP[activeTabRef.current],
        };
        const result = await searchUsersRequest(params);
        setTotalCount(result?.value?.totalCount || 0);
        setUsers(result?.value?.rows || []);
        setCurrentPage(page);
      } catch (error) {
        snackbar.error('Failed to load users');
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [snackbar, pageSize],
  );

  // debounced search function
  const debouncedFetchUsers = useMemo(
    () =>
      debounce((page, query, status) => {
        fetchUsers(page, query, status);
      }, 500),
    [fetchUsers],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedFetchUsers(1, value, status);
  };

  const onStatusChange = (value: unknown) => {
    const statusValue = value as number | undefined;
    setStatus(statusValue);
    fetchUsers(1, searchText, statusValue);
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setUsers([]);
  }, [activeTabRef.current]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchUsers(page, searchText, status);
  };

  const handleUserSelect = (user: UserType) =>
    navigate(`${USERMANAGEMENTPATH}/${user.userId}`);
  const handleAddNew = () => setModalVisible(true);

  const handleEditUser = (user: UserType) => {
    setEditingUser(user);
    setModalVisible(true);
  };

  const handleAddSubmit = async (values: any) => {
    try {
      setLoading(true);
      const newUserData = {
        fullName: values.fullName?.trim(),
        email: values.email?.trim(),
        phoneNumber: values.phoneNumber?.trim(),
        bio: values.bio?.trim() || '',
        role: values.userRole || 4,
        profilePictureUrl: values.profilePicture?.file?.originFileObj || '',
        entityStatus: values.entityStatus,
      } as CreateUserByAdminType;
      const newUserResponse = await createUserAdminRequest(newUserData);
      snackbar.success('User created successfully');
      setModalVisible(false);
      fetchUsers(1, searchText, status);
    } catch (error: any) {
      snackbar.error(error?.Error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (values: any) => {
    if (!editingUser) {
      snackbar.error('No user selected for editing');
      return;
    }
    try {
      setLoading(true);
      const updateData = {
        fullName: values.fullName?.trim(),
        email: values.email?.trim(),
        phoneNumber: values.phoneNumber?.trim(),
        bio: values.bio?.trim() || '',
        role: values.userRole,
        profilePictureUrl: values.profilePicture?.file?.originFileObj || '',
        entityStatus: values.entityStatus,
      } as CreateUserByAdminType;
      await updateUserAdminRequest(editingUser.userId, updateData);
      snackbar.success('User updated successfully');
      setModalVisible(false);
      setEditingUser(null);
      fetchUsers(currentPage, searchText, status);
    } catch (error: any) {
      snackbar.error(error.Error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingUser(null);
  };
  const handleDeleteClick = (user: UserType) => {
    setOpenDeleteModal(true);
    setSelectedUserToDelete(user);
  };
  const handleDeleteModalClose = () => {
    setOpenDeleteModal(false);
    setSelectedUserToDelete(null);
  };

  const tableColumns: ColumnType<UserType>[] = [
    {
      title: 'Username',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (fullName: string, user: UserType) => (
        <Button
          type="link"
          disabled={loading || user.entityStatus === -1}
          onClick={() => handleUserSelect(user)}
          style={{
            padding: 0,
            height: 'auto',
            fontSize: '14px',
          }}
        >
          {fullName || 'N/A'}
        </Button>
      ),
    },
    {
      title: 'Email ID',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || 'N/A',
    },
    {
      title: 'Contact No',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber: string) => phoneNumber || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'entityStatus',
      key: 'entityStatus',
      render: (entityStatus: number) => {
        const status =
          entityStatus === 1
            ? 'Active'
            : entityStatus === -1
              ? 'Deleted'
              : 'Inactive';
        const colorMap = {
          Active: 'cyan',
          Deleted: 'red',
          Inactive: 'orange',
        };
        return (
          <CustomTag color={colorMap[status as keyof typeof colorMap]}>
            {status}
          </CustomTag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, user: UserType) => (
        <StyledSpaceWrapper>
          <StyledActionButton
            type="link"
            onClick={() => handleEditUser(user)}
            disabled={loading || user.entityStatus === -1}
          >
            Edit
          </StyledActionButton>
          <StyledActionButton
            type="link"
            onClick={() => handleDeleteClick(user)}
            disabled={loading || user.entityStatus === -1}
          >
            Delete
          </StyledActionButton>
        </StyledSpaceWrapper>
      ),
    },
  ];

  const CustomHeaderCell = (props: any) => {
    const { style, ...otherProps } = props;
    return <th {...otherProps} style={{ ...style, ...tableHeaderStyle }} />;
  };

  if (outlet) {
    return <Outlet />;
  }

  return (
    <MainContainer>
      <Header>
        <div style={{ flex: 1 }} />
        <StyledSpace direction="horizontal" size={24}>
          <StyledSearch
            placeholder="Search by User, Email, Phone..."
            onChange={handleSearchChange}
            enterButton={<SearchOutlined />}
            size="large"
            allowClear
            value={searchText}
          />
          <StyledSelect
            value={status}
            onChange={onStatusChange}
            placeholder="Select Status"
            allowClear
          >
            {USER_ENTITY_STATUS_OPTIONS.map((option) => (
              <Option key={option.label} value={option.value}>
                {option.label}
              </Option>
            ))}
          </StyledSelect>
          <CustomButton
            icon={<PlusCircleOutlined style={{ fontSize: TEXT_SIZES.LG }} />}
            onClick={handleAddNew}
            width="182px"
            type="primary"
          >
            Add New User
          </CustomButton>
        </StyledSpace>
      </Header>
      <StyledTabs
        activeKey={activeTabRef.current}
        onChange={handleTabChange}
        style={{ marginBottom: '16px' }}
        items={USER_TABS}
      />
      <CustomTable
        rowKey="userId"
        columns={tableColumns}
        dataSource={users}
        pagination={{
          pageSize: pageSize,
          current: currentPage,
          total: totalCount,
          onChange: (page) => handlePageChange(page),
        }}
        loading={loading}
        bordered
        components={{ header: { cell: CustomHeaderCell } }}
      />
      <UserForm
        open={modalVisible}
        onClose={handleCancel}
        onSubmit={editingUser ? handleEditSubmit : handleAddSubmit}
        initialValues={
          editingUser
            ? {
                ...editingUser,
                fullName: editingUser.fullName ?? undefined,
                email: editingUser.email ?? undefined,
                phoneNumber: editingUser.phoneNumber ?? undefined,
                roleId: editingUser.userRole ?? undefined,
              }
            : null
        }
      />
      <DeleteModal
        open={openDeleteModal}
        moduleType="user"
        body={selectedUserToDelete?.fullName || ''}
        onCancel={handleDeleteModalClose}
        onConfirm={onDeleteUser}
      />
    </MainContainer>
  );
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const StyledSpace = styled(Space)`
  width: 100%;
  & .ant-space-item:first-child {
    flex: 1;
  }
`;

export const StyledSearch = styled(Search)`
  flex: 1;
  .ant-btn-variant-solid {
    background-color: ${COLORS.blue.primary};
  }
`;

const MainContainer = styled.div`
  padding: 24px;
  min-height: 100vh;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    font-weight: ${FONT_WEIGHTS.MEDIUM};
  }
  .ant-tabs-tab-active {
    color: ${COLORS.skyBlue.primary} !important;
  }
  .ant-tabs-ink-bar {
    background-color: ${COLORS.skyBlue.primary};
  }
`;

const tableHeaderStyle = {
  backgroundColor: `${COLORS.blue.primary}`,
  color: `${COLORS.white.primary}`,
  fontWeight: 600,
  borderColor: `${COLORS.blue.primary}`,
};

export const StyledSelect = styled(Select)`
  width: 150px;
  .ant-select-selector {
    height: 40px !important;
    display: flex;
    align-items: center;
    margin-top: -4px;
  }
`;

export default UserManagement;
